import { useEffect, useState } from "react";
import { CreditCard, RefreshCw, Copy, MessageCircle, ExternalLink, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function publicUrl(code) {
  return `${window.location.origin}/pagos/${encodeURIComponent(code)}`;
}

function cleanPhone(phone) {
  return String(phone || "").replace(/\D/g, "").replace(/^34/, "");
}

function paymentTypeLabel(type) {
  if (type === "deposit") return "Señal";
  if (type === "partial") return "Parcial";
  if (type === "full") return "Completo";
  return "Pago";
}

function remainingAmount(item) {
  const total = Number(item.total_amount_eur || 0);
  const paidOrRequested = Number(item.amount_eur || 0);
  if (!total || total <= paidOrRequested) return 0;
  return Math.max(0, total - paidOrRequested);
}

function formatDateTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("es-ES");
  } catch {
    return "";
  }
}

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("active");
  const [form, setForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    description: "",
    delivery_date: "",
    total_amount_eur: "",
    amount_eur: "",
    payment_type: "deposit",
    payment_note: "",
    parent_code: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manual-payments");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    const amount = Number(String(form.amount_eur || "").replace(",", "."));
    if (!form.client_name.trim()) return toast.error("Falta el nombre del cliente");
    if (!form.description.trim()) return toast.error("Falta la descripción");
    if (!amount || Number.isNaN(amount) || amount <= 0) return toast.error("Importe solicitado no válido");

    setCreating(true);
    try {
      const payload = {
        ...form,
        amount_eur: amount,
        total_amount_eur: form.total_amount_eur ? Number(String(form.total_amount_eur).replace(",", ".")) : null,
      };
      const { data } = await api.post("/admin/manual-payments", payload);
      toast.success(`Cobro ${data.code} creado`);
      setItems((prev) => [data, ...prev]);
      setForm({
        client_name: "",
        client_phone: "",
        client_email: "",
        description: "",
        delivery_date: "",
        total_amount_eur: "",
        amount_eur: "",
        payment_type: "deposit",
        payment_note: "",
        parent_code: "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el cobro");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(publicUrl(code));
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const whatsapp = (item) => {
    const url = publicUrl(item.code);
    const amount = Number(item.amount_eur || 0).toFixed(2);
    const kind = paymentTypeLabel(item.payment_type).toLowerCase();
    const rest = remainingAmount(item);
    const totalText = item.total_amount_eur ? ` Total estimado del trabajo: ${Number(item.total_amount_eur).toFixed(2)} €.` : "";
    const restText = rest > 0 ? ` Resto pendiente después de este pago: ${rest.toFixed(2)} €.` : "";
    const text = `Hola ${item.client_name || ""}, puedes realizar el pago seguro de KAPEI (${kind}) por ${amount} € aquí: ${url}.${totalText}${restText}`;
    const phone = cleanPhone(item.client_phone);
    const target = phone
      ? `https://wa.me/34${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const createRemainingPayment = async (item) => {
    const rest = remainingAmount(item);
    if (!rest) return;

    if (!window.confirm(`¿Crear un nuevo cobro por el resto pendiente (${rest.toFixed(2)} €)?`)) return;

    const payload = {
      client_name: item.client_name || "",
      client_phone: item.client_phone || "",
      client_email: item.client_email || "",
      description: `Resto pendiente de ${item.description || item.code}`,
      delivery_date: item.delivery_date || "",
      total_amount_eur: item.total_amount_eur || null,
      amount_eur: Number(rest.toFixed(2)),
      payment_type: "partial",
      payment_note: `Pago del resto pendiente. Total estimado: ${Number(item.total_amount_eur || 0).toFixed(2)} €. Ya solicitado/pagado: ${Number(item.amount_eur || 0).toFixed(2)} €. Resto: ${rest.toFixed(2)} €.`,
      parent_code: item.parent_code || item.code,
    };

    try {
      setCreating(true);
      const { data } = await api.post("/admin/manual-payments", payload);
      setItems((prev) => [data, ...prev]);
      toast.success(`Cobro del resto creado: ${data.code}`);
      setFilter("all");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el cobro del resto");
    } finally {
      setCreating(false);
    }
  };

  const cancel = async (item) => {
    if (!window.confirm(`¿Cancelar el cobro ${item.code}?`)) return;
    try {
      const { data } = await api.post(`/admin/manual-payments/${encodeURIComponent(item.code)}/cancel`);
      setItems((prev) => prev.map((x) => x.code === item.code ? data : x));
      toast.success("Cobro cancelado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cancelar");
    }
  };

  const rootCode = (item) => item.parent_code || item.code;

  const groupedItems = items.reduce((acc, item) => {
    const key = rootCode(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const rootItems = items.filter((item) => !item.parent_code);

  const groupPaidAmount = (item) => {
    const group = groupedItems[item.code] || [item];
    return group
      .filter((x) => x.status === "paid")
      .reduce((sum, x) => sum + Number(x.amount_eur || 0), 0);
  };

  const groupRequestedAmount = (item) => {
    const group = groupedItems[item.code] || [item];
    return group.reduce((sum, x) => sum + Number(x.amount_eur || 0), 0);
  };

  const groupRemainingAmount = (item) => {
    const total = Number(item.total_amount_eur || 0);
    if (!total) return remainingAmount(item);
    return Math.max(0, total - groupPaidAmount(item));
  };

  const filteredItems = rootItems.filter((item) => {
    if (filter === "active") return item.status !== "cancelled";
    if (filter === "pending") return item.status === "pending";
    if (filter === "paid") return item.status === "paid";
    if (filter === "cancelled") return item.status === "cancelled";
    if (filter === "deposit") return item.payment_type === "deposit" && item.status !== "cancelled";
    if (filter === "full") return item.payment_type === "full" && item.status !== "cancelled";
    if (filter === "partial") return item.payment_type === "partial" && item.status !== "cancelled";
    return true;
  });

  const summary = items.reduce((acc, item) => {
    const amount = Number(item.amount_eur || 0);
    if (item.status === "pending") {
      acc.pendingCount += 1;
      acc.pendingAmount += amount;
    }
    if (item.status === "paid") {
      acc.paidCount += 1;
      acc.paidAmount += amount;
    }
    return acc;
  }, { pendingCount: 0, pendingAmount: 0, paidCount: 0, paidAmount: 0 });

  const stripeFeeApprox = (() => {
    const amount = Number(String(form.amount_eur || "").replace(",", "."));
    if (!amount || Number.isNaN(amount)) return null;
    // Estimación orientativa, configurable más adelante.
    return amount * 0.015 + 0.25;
  })();

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Cobros KAPEI</h1>
          <p className="mt-1 text-sm text-slate-600">
            Crea señales, pagos parciales o pagos completos para clientes sin app.
          </p>
        </div>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
        </Button>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-lg font-black text-slate-900">
          <CreditCard className="h-5 w-5 text-[#4d7c0f]" />
          Nuevo cobro
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input placeholder="Cliente" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} />
          <Input placeholder="Teléfono" value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} />
          <Input placeholder="Email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} />

          <Input className="md:col-span-2" placeholder="Descripción del trabajo" value={form.description} onChange={(e) => set("description", e.target.value)} />
          <Input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} />

          <Input placeholder="Importe total estimado" value={form.total_amount_eur} onChange={(e) => set("total_amount_eur", e.target.value)} />
          <Input placeholder="Importe solicitado" value={form.amount_eur} onChange={(e) => set("amount_eur", e.target.value)} />
          <select
            value={form.payment_type}
            onChange={(e) => set("payment_type", e.target.value)}
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold"
          >
            <option value="deposit">Señal / reserva</option>
            <option value="partial">Pago parcial</option>
            <option value="full">Pago completo</option>
          </select>

          <Input
            className="md:col-span-3"
            placeholder="Texto visible para cliente. Ej.: Señal de 20 €. Total estimado 80 €."
            value={form.payment_note}
            onChange={(e) => set("payment_note", e.target.value)}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-semibold text-slate-500">
            {stripeFeeApprox != null && (
              <>Comisión Stripe estimada: <strong>{stripeFeeApprox.toFixed(2)} €</strong> · importe neto aprox.: <strong>{(Number(String(form.amount_eur || "0").replace(",", ".")) - stripeFeeApprox).toFixed(2)} €</strong></>
            )}
          </div>
          <Button onClick={create} disabled={creating} className="bg-[#4d7c0f] font-black hover:bg-[#3f6212]">
            {creating ? "Creando…" : "Crear cobro y enlace"}
          </Button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <div className="text-xs font-black uppercase tracking-widest text-blue-700">Pendientes</div>
          <div className="mt-1 text-2xl font-black text-blue-950">{summary.pendingCount}</div>
        </div>
        <div className="rounded-2xl border border-blue-200 bg-white p-4">
          <div className="text-xs font-black uppercase tracking-widest text-blue-700">Importe pendiente</div>
          <div className="mt-1 text-2xl font-black text-blue-950">{summary.pendingAmount.toFixed(2)} €</div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Pagados</div>
          <div className="mt-1 text-2xl font-black text-emerald-950">{summary.paidCount}</div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-white p-4">
          <div className="text-xs font-black uppercase tracking-widest text-emerald-700">Importe cobrado</div>
          <div className="mt-1 text-2xl font-black text-emerald-950">{summary.paidAmount.toFixed(2)} €</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
          <div className="font-black text-slate-900">Últimos cobros</div>
          <div className="flex flex-wrap gap-2">
            {[
              ["active", "Activos"],
              ["all", "Todos"],
              ["pending", "Pendientes"],
              ["paid", "Pagados"],
              ["deposit", "Señales"],
              ["partial", "Parciales"],
              ["full", "Completos"],
              ["cancelled", "Cancelados"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full px-3 py-1 text-xs font-black ${
                  filter === key ? "bg-[#4d7c0f] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500">Cargando…</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-6 text-center text-slate-500">No hay cobros todavía.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredItems.map((item) => (
              <div key={item.code} className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_auto]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-lg font-black text-[#4d7c0f]">{item.code}</span>

                    <span className={`rounded-full px-2 py-0.5 text-xs font-black uppercase ${
                      item.payment_type === "deposit" ? "bg-amber-100 text-amber-800" :
                      item.payment_type === "partial" ? "bg-purple-100 text-purple-800" :
                      "bg-slate-100 text-slate-800"
                    }`}>
                      {paymentTypeLabel(item.payment_type)}
                    </span>

                    <span className={`rounded-full px-2 py-0.5 text-xs font-black uppercase ${
                      item.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                      item.status === "cancelled" ? "bg-red-100 text-red-800" :
                      "bg-blue-100 text-blue-800"
                    }`}>
                      {item.status === "paid" ? "Pagado" : item.status === "cancelled" ? "Cancelado" : "Pendiente"}
                    </span>

                    {item.status === "paid" && item.paid_at && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-800">
                        Pagado el {formatDateTime(item.paid_at)}
                      </span>
                    )}
                  </div>

                  <div className="mt-1 text-sm font-bold text-slate-900">{item.client_name}</div>
                  <div className="text-xs text-slate-500">{item.description}</div>

                  <div className="mt-3 grid grid-cols-1 gap-2 text-sm font-bold sm:grid-cols-3">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total trabajo</div>
                      <div className="text-lg font-black text-slate-900">
                        {item.total_amount_eur ? `${Number(item.total_amount_eur).toFixed(2)} €` : "—"}
                      </div>
                    </div>

                    <div className="rounded-xl bg-blue-50 p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-blue-700">
                        {item.status === "paid" ? "Pagado" : "Solicitado"}
                      </div>
                      <div className="text-lg font-black text-blue-950">
                        {Number(item.amount_eur || 0).toFixed(2)} €
                      </div>
                    </div>

                    <div className="rounded-xl bg-orange-50 p-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-orange-700">Resto pendiente</div>
                      <div className="text-lg font-black text-orange-950">
                        {groupRemainingAmount(item) > 0 ? `${groupRemainingAmount(item).toFixed(2)} €` : "—"}
                      </div>
                    </div>
                  </div>

                  {groupedItems[item.code]?.length > 1 && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Historial de pagos vinculados
                      </div>
                      <div className="space-y-2">
                        {groupedItems[item.code].map((child) => (
                          <div key={child.code} className="rounded-lg bg-white p-2 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <span className="font-mono font-black text-[#4d7c0f]">{child.code}</span>
                                <span className="ml-2 font-bold text-slate-700">{paymentTypeLabel(child.payment_type)}</span>
                                <span className="ml-2 text-slate-500">{child.description}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-black">{Number(child.amount_eur || 0).toFixed(2)} €</span>
                                <span className={`rounded-full px-2 py-0.5 font-black uppercase ${
                                  child.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                                  child.status === "cancelled" ? "bg-red-100 text-red-800" :
                                  "bg-blue-100 text-blue-800"
                                }`}>
                                  {child.status === "paid" ? "Pagado" : child.status === "cancelled" ? "Cancelado" : "Pendiente"}
                                </span>
                              </div>
                            </div>

                            <div className="mt-2 flex flex-wrap gap-2">
                              <Button variant="outline" size="sm" onClick={() => copy(child.code)}>
                                <Copy className="mr-1 h-4 w-4" /> Copiar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => whatsapp(child)}>
                                <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp
                              </Button>
                              <a
                                href={publicUrl(child.code)}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold hover:bg-slate-50"
                              >
                                <ExternalLink className="mr-1 inline h-4 w-4" /> Abrir
                              </a>
                              {child.status === "pending" && (
                                <Button variant="outline" size="sm" onClick={() => cancel(child)} className="text-red-700 hover:bg-red-50">
                                  <XCircle className="mr-1 h-4 w-4" /> Cancelar
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-2 break-all text-xs font-semibold text-blue-700">{publicUrl(item.code)}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:justify-end">
                  <Button variant="outline" size="sm" onClick={() => copy(item.code)}>
                    <Copy className="mr-1 h-4 w-4" /> Copiar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => whatsapp(item)}>
                    <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp
                  </Button>
                  <a
                    href={publicUrl(item.code)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold hover:bg-slate-50"
                  >
                    <ExternalLink className="mr-1 inline h-4 w-4" /> Abrir
                  </a>
                  {item.status === "paid" && groupRemainingAmount(item) > 0 && (
                    <Button variant="outline" size="sm" onClick={() => createRemainingPayment({ ...item, amount_eur: groupPaidAmount(item) || item.amount_eur })} className="text-purple-700 hover:bg-purple-50">
                      Crear cobro del resto ({groupRemainingAmount(item).toFixed(2)} €)
                    </Button>
                  )}
                  {item.status === "pending" && (
                    <Button variant="outline" size="sm" onClick={() => cancel(item)} className="text-red-700 hover:bg-red-50">
                      <XCircle className="mr-1 h-4 w-4" /> Cancelar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
